// Dev preview for the 16-18 report.
// URL: /report/16-18/preview?seed=1&name=Tima&lang=ru
//
// Mirrors the 14-15 preview but uses Report12Page (Phase 3 expanded
// to 10 pages with new "Что делать" page 9 and removed "Педагогу" column).

import { createFileRoute, Link } from "@tanstack/react-router";
import { Report12Page } from "@/components/report/Report12Page";
import {
  buildMockSessionResult,
  meaningOverrides,
  parseMeaningSearch,
  type MeaningPreview,
} from "@/lib/preview-mock";
import type { LanguageKey, ArchetypeCode } from "@/lib/yolutap/types";

type PreviewSearch = {
  seed: number;
  name: string;
  age: number;
  lang: LanguageKey;
  primary?: ArchetypeCode;
  secondary?: ArchetypeCode;
  // PREVIEW-MOCK-ONLY B-wire verification overrides:
  // mixed_profile=1 → forces mixed_profile=true (обложка надпись больше не показывает — с 2026-10 вместо неё правило «профиль пока не ярко выражен», src/lib/weak-profile.ts)
  // tie=0 → forces tie_group=[] (hides chip-plus)
  // Real session rendering reads these from calculateResult — unaffected.
  mixedProfile?: boolean;
  tieGroup?: ArchetypeCode[];
  weak?: boolean;
  // Варианты смысла (13.10): ?sphere=one:H | two:S,H | broad, ?lead_tie=1, ?close=K
  meaning?: MeaningPreview;
};

export const Route = createFileRoute("/report/16-18/preview")({
  validateSearch: (search: Record<string, unknown>): PreviewSearch => ({
    seed:
      typeof search.seed === "number"
        ? search.seed
        : typeof search.seed === "string"
          ? parseInt(search.seed, 10) || 1
          : 1,
    name: typeof search.name === "string" && search.name ? search.name : "Tima",
    age:
      typeof search.age === "number"
        ? search.age
        : typeof search.age === "string"
          ? parseInt(search.age, 10) || 17
          : 17,
    lang: (typeof search.lang === "string" && ["ru", "az", "en"].includes(search.lang)
      ? search.lang
      : "ru") as LanguageKey,
    primary: typeof search.primary === "string" ? (search.primary as ArchetypeCode) : undefined,
    secondary:
      typeof search.secondary === "string" ? (search.secondary as ArchetypeCode) : undefined,
    mixedProfile:
      search.mixed_profile === "1" || search.mixed_profile === 1 || search.mixed_profile === true
        ? true
        : search.mixed_profile === "0" || search.mixed_profile === 0 || search.mixed_profile === false
          ? false
          : undefined,
    tieGroup: search.tie === "0" || search.tie === 0 ? [] : undefined,
    weak: search.weak === "1" || search.weak === 1 || search.weak === true ? true : undefined,
    meaning: parseMeaningSearch(search),
  }),
  head: () => ({
    meta: [
      { title: "Yolutap · Отчёт 16–18 — предпросмотр" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: PreviewPage,
});

function PreviewPage() {
  const { seed, name, age, lang, primary, secondary, mixedProfile, tieGroup, weak, meaning } =
    Route.useSearch();
  const data = buildMockSessionResult({
    seed,
    name,
    age,
    lang,
    ageGroup: "16_18",
    primaryCode: primary,
    secondaryCode: secondary,
    mixedProfileOverride: mixedProfile,
    tieGroupOverride: tieGroup,
    weakOverride: weak,
    ...meaningOverrides(meaning),
  });

  const langBtn = (l: LanguageKey, label: string) => (
    <Link
      key={l}
      to="/report/16-18/preview"
      search={{ seed, name, age, lang: l, primary, secondary }}
      style={{
        padding: "6px 12px",
        borderRadius: 6,
        background: lang === l ? "#1E5A6B" : "#fff",
        color: lang === l ? "#fff" : "#1E5A6B",
        textDecoration: "none",
        border: "1px solid #1E5A6B",
        fontSize: 13,
      }}
    >
      {label}
    </Link>
  );

  return (
    <>
      {/*
        Print scope: strip the dev chrome (toolbar + cream-card framing).
        Without this, the outer padding/minHeight and the toolbar height
        push .report ~70px down, and Chrome adds a leading blank paper
        page (toolbar takes paper page 1) plus a trailing blank from the
        24px bottom padding — total 12 paper pages instead of the
        designed 10. Same fix as report.14-15.preview.tsx.
      */}
      <style>{`
        @media print {
          .preview-frame { padding: 0 !important; background: transparent !important; min-height: 0 !important; }
          .preview-toolbar { display: none !important; }
          .preview-card { box-shadow: none !important; max-width: none !important; margin: 0 !important; }
        }
      `}</style>
      <div className="preview-frame" style={{ padding: 24, background: "#E7E2D6", minHeight: "100vh" }}>
        <div
          className="preview-toolbar"
          style={{
            maxWidth: 900,
            margin: "0 auto",
            marginBottom: 20,
            display: "flex",
            gap: 16,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <Link
            to="/report/16-18/preview"
            search={{ seed: seed + 1, name, age, lang, primary, secondary }}
            style={{
              background: "#1E5A6B",
              color: "#fff",
              padding: "8px 16px",
              borderRadius: 6,
              textDecoration: "none",
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            ↻ Новый случайный (seed +1)
          </Link>
          {langBtn("ru", "RU")}
          {langBtn("az", "AZ")}
          {langBtn("en", "EN")}
          <span style={{ fontSize: 12, color: "#6B8090", marginLeft: "auto" }}>
            seed={seed} · {data.primary.code}+{data.secondary.code}
          </span>
        </div>

        <div
          className="preview-card"
          style={{
            background: "#fff",
            boxShadow: "0 24px 48px -24px rgba(15,42,56,.25)",
            maxWidth: 900,
            margin: "0 auto",
          }}
        >
          <Report12Page data={data} />
        </div>
      </div>
    </>
  );
}
